import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, model } from '@angular/core';
import {
  ValidatorFn,
  ValidationErrors,
  AbstractControl,
  FormGroup
} from '@angular/forms';
import { MinMaxService } from '@app/components/range-kompleksni/services/min-max-share.service';
import { PrimeNGInputDateRangeComponent } from '@ntap/primeng';

export type VrijemeInputOption = 'Godina' | 'Datum';

@Component({
  selector: 'app-vrijeme-range-kompleksni',
  templateUrl: './vrijeme-range-kompleksni.component.html',
  styleUrls: ['./vrijeme-range-kompleksni.component.css'],
  providers: [MinMaxService]
})
export class VrijemeRangeKompleksniComponent
  extends PrimeNGInputDateRangeComponent
  implements AfterViewInit
{
  selectedOption = model<VrijemeInputOption>();
  options: VrijemeInputOption[] = ['Godina', 'Datum'];
  showInOverlay = false;
  formGroup: FormGroup;
  datepipe = new DatePipe('hr-HR');

  override ngAfterViewInit() {
    this.initializeSelectedOption();
    this.addCustomValidator();
  }

  private initializeSelectedOption() {
    if (!this.inputParam?.objectData) {
      this.selectedOption.set('Datum');
      return;
    }
    const objectData = this.inputParam.objectData;
    const selectedOption = this.hasGodinaData(objectData) ? 'Godina' : 'Datum';
    this.selectedOption.set(selectedOption);
  }

  private hasGodinaData(objectData: any) {
    const godinaAttrName = `${this.getAttrName()}_godina`;
    const godinaFromAttrName = `${godinaAttrName}$from`;
    const godinaToAttrName = `${godinaAttrName}$to`;
    return (
      (objectData[godinaFromAttrName] !== undefined &&
        objectData[godinaFromAttrName] != null) ||
      (objectData[godinaToAttrName] !== undefined &&
        objectData[godinaToAttrName] != null)
    );
  }

  private addCustomValidator(): void {
    this.formGroup = this.formService.formGroup;

    const { godinaOd, godinaDo, datumOd, datumDo } =
      this.getInputNamesBezSufiksa();

    const datumFromControl = this.formService.getFormControl(datumOd);
    const datumToControl = this.formService.getFormControl(datumDo);
    const godinaFromControl = this.formService.getFormControl(godinaOd);
    const godinaToControl = this.formService.getFormControl(godinaDo);

    if (!this.inputParam.attributeMetadata.read_only) {
      this.formService.addValidators(godinaOd, [
        this.validateFrom(godinaToControl)
      ]);
      this.formService.addValidators(datumOd, [
        this.validateFrom(datumToControl)
      ]);
      this.formService.addValidators(godinaDo, [
        this.validateTo(godinaFromControl)
      ]);
      this.formService.addValidators(datumDo, [
        this.validateTo(datumFromControl)
      ]);
      this.formGroup.updateValueAndValidity();
    }
  }
  private getInputNamesBezSufiksa(): {
    godinaOd: string;
    godinaDo: string;
    datumOd: string;
    datumDo: string;
  } {
    const attrName = this.getAttrName();

    return {
      godinaOd: attrName + '_godina' + '$from',
      godinaDo: attrName + '_godina' + '$to',
      datumOd: attrName + '$from',
      datumDo: attrName + '$to'
    };
  }

  private getAttrName() {
    const name = this.inputParam.attributeMetadata.name;
    const indexOfSuffix = name.lastIndexOf('$');
    const attrName =
      indexOfSuffix < 0 ? name : name.substring(0, indexOfSuffix);
    return attrName;
  }

  private validateFrom(toFormControl: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        toFormControl.value !== null &&
        toFormControl.value !== undefined &&
        control.value !== null &&
        control.value !== undefined &&
        toFormControl.value < control.value
      ) {
        return { rangeFromInvalid: true };
      } else if (
        !(control.value instanceof Date) &&
        control.value !== null &&
        control.value !== undefined &&
        (control.value > 9999 || control.value <= 0)
      ) {
        return { minMaxInvalid: true };
      } else if (
        control.value instanceof Date &&
        control.value !== null &&
        control.value !== undefined &&
        (control.value > new Date(9999, 11, 31, 23, 59, 59) ||
          control.value < new Date(-1, 11, 31, 23, 59, 59))
      ) {
        return { minMaxInvalid: true };
      }
      return null;
    };
  }

  private validateTo(fromFormControl: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        fromFormControl.value !== null &&
        fromFormControl.value !== undefined &&
        control.value !== null &&
        control.value !== undefined &&
        control.value < fromFormControl.value
      ) {
        return { rangeToInvalid: true };
      } else if (
        !(control.value instanceof Date) &&
        control.value !== null &&
        control.value !== undefined &&
        (control.value > 9999 || control.value <= 0)
      ) {
        return { minMaxInvalid: true };
      } else if (
        control.value instanceof Date &&
        control.value !== null &&
        control.value !== undefined &&
        (control.value > new Date(9999, 11, 31, 23, 59, 59) ||
          control.value < new Date(-1, 11, 31, 23, 59, 59))
      ) {
        return { minMaxInvalid: true };
      }
      return null;
    };
  }

  override doChangedInputParam(): void {
    this.showInOverlay = this.inputParam.showInOverlay || false;
  }
  getVrijemeNastankaLabel() {
    const g = this.selectedOption() === 'Godina' ? ' g.' : '';
    return `${this.getVrijemeNastankaStart()}-${this.getVrijemeNastankaEnd()}${g}`;
  }

  getVrijemeNastankaStart(): string {
    if (!this.inputParam?.objectData) {
      return '';
    }
    const objectData = this.inputParam.objectData;
    const { godinaOd, godinaDo, datumOd, datumDo } =
      this.getInputNamesBezSufiksa();
    let start: string = '';
    if (this.selectedOption() === 'Godina') {
      start = objectData[godinaOd]?.toString() || '';
    } else if (this.selectedOption() === 'Datum') {
      start = this.datepipe.transform(objectData[datumOd], 'dd.MM.yyyy.') || '';
    }
    if (!start) {
      return '';
    }
    return start;
  }

  getVrijemeNastankaEnd(): string {
    if (!this.inputParam?.objectData) {
      return '';
    }
    const objectData = this.inputParam.objectData;
    const { godinaOd, godinaDo, datumOd, datumDo } =
      this.getInputNamesBezSufiksa();
    let end: string = '';
    if (this.selectedOption() === 'Godina') {
      end = objectData[godinaDo]?.toString() || '';
    } else if (this.selectedOption() === 'Datum') {
      end = this.datepipe.transform(objectData[datumDo], 'dd.MM.yyyy.') || '';
    }
    if (!end) {
      return '';
    }
    return end;
  }
}
